/**
 * LoginPage Component
 *
 * User login page with email/password authentication.
 * Includes form validation, error handling, and redirect to register.
 *
 * Usage:
 *   <Route path="/login" element={<LoginPage />} />
 */

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AuthShell, AuthTitle, AuthField, AuthSubmit, AuthNote, authLink } from '@/components/auth/AuthShell';
import { useAuth } from '@/hooks/useAuth';

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const [errors, setErrors] = useState<{
    email?: string;
    password?: string;
    general?: string;
  }>({});

  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      await login(formData.email, formData.password, rememberMe);
      // Navigation happens automatically via AuthContext
      navigate('/');
    } catch (err: any) {
      console.error('Login error:', err);
      setErrors({
        general: err.message || 'Invalid email or password. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell headline={<>Welcome back.<br />Your month is waiting.</>} sub="Plans, ads and results for every business you run, in one place.">
      <AuthTitle>Sign in</AuthTitle>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
        {errors.general && <AuthNote>{errors.general}</AuthNote>}
        <AuthField label="Email" name="email" type="email" value={formData.email} onChange={handleChange}
          placeholder="you@example.com" error={errors.email} autoComplete="email" autoFocus />
        <AuthField label="Password" name="password" type="password" value={formData.password} onChange={handleChange}
          placeholder="••••••••" error={errors.password} autoComplete="current-password"
          aside={<Link to="/forgot-password" className={`text-sm ${authLink}`}>Forgot?</Link>} />
        <label className="flex cursor-pointer items-center gap-2.5 text-sm text-[#2B3346]">
          <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} className="h-[18px] w-[18px] accent-[#0F1526]" />
          Keep me signed in
        </label>
        <AuthSubmit loading={loading}>Sign in</AuthSubmit>
        <p className="m-0 text-center text-sm text-[#4A5468]">
          New to Admeru? <Link to="/register" className={authLink}>Create an account</Link>
        </p>
      </form>
    </AuthShell>
  );
};

export default LoginPage;
